import React, { ReactNode } from 'react';
import { cn } from '../utils/cn';

export interface CenterPageProps {
  children: ReactNode;
  className?: string;
}

export interface CenterPageHeaderProps {
  children: ReactNode;
  className?: string;
}

export interface CenterPageSectionProps {
  children: ReactNode;
  className?: string;
  spacing?: 'sm' | 'md' | 'lg';
}

/**
 * CenterPage component for consistent page layout
 * Provides responsive padding and spacing for content pages
 */
export const CenterPage: React.FC<CenterPageProps> & {
  Header: React.FC<CenterPageHeaderProps>;
  Section: React.FC<CenterPageSectionProps>;
} = ({ children, className }) => {
  return (
    <div
      className={cn(
        'min-h-full',
        'px-4 sm:px-6 lg:px-8',
        'py-8 sm:py-12 lg:py-16',
        'max-w-7xl mx-auto',
        className
      )}
    >
      {children}
    </div>
  );
};

/**
 * Page header section with title and optional subtitle
 */
const CenterPageHeader: React.FC<CenterPageHeaderProps> = ({ children, className }) => {
  return <header className={cn('mb-8 sm:mb-12', className)}>{children}</header>;
};

/**
 * Content section with consistent spacing
 */
const CenterPageSection: React.FC<CenterPageSectionProps> = ({
  children,
  className,
  spacing = 'md',
}) => {
  const spacingClasses = {
    sm: 'mt-4 sm:mt-6',
    md: 'mt-6 sm:mt-8',
    lg: 'mt-8 sm:mt-12',
  };

  return <section className={cn(spacingClasses[spacing], className)}>{children}</section>;
};

// Attach sub-components
CenterPage.Header = CenterPageHeader;
CenterPage.Section = CenterPageSection;

// Display names for debugging
CenterPage.displayName = 'CenterPage';
CenterPageHeader.displayName = 'CenterPage.Header';
CenterPageSection.displayName = 'CenterPage.Section';
